import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaNetworkWired } from '@react-icons/all-files/fa/FaNetworkWired';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageForm, { FormField } from '../../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../manager/workflow-empresa.reducer';
import { getEntities as getClases } from '../../../../manager/workflow-clase.reducer';
import { getEntities as getWorkflows } from '../../../../manager/workflow-def.reducer';
import { getEntities as getGrupos } from '../../../../manager/grupo/grupo-empresarial.reducer';

const BACK = '/configuracion/workflow/workflow-empresa';

interface WEForm {
  id: string;
  empresa: string;
  grupo: string;
  workflow: string;
  clase: string;
  activo: string;
  fechaInicio: string;
  disenoPersonalizado: string;
}

const empty: WEForm = { id: '', empresa: '', grupo: '', workflow: '', clase: '', activo: 'true', fechaInicio: '', disenoPersonalizado: '' };

export default function WorkflowEmpresaFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.workflowEmpresa.entity) as any;
  const updating = useAppSelector(s => s.workflowEmpresa.updating);
  const updateSuccess = useAppSelector(s => s.workflowEmpresa.updateSuccess);

  const grupos = useAppSelector(s => s.grupoEmpresarial.entities) as any[];
  const workflows = useAppSelector(s => s.workflowDef.entities) as any[];
  const clases = useAppSelector(s => s.workflowClase.entities) as any[];

  const [form, setForm] = useState<WEForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof WEForm, string>>>({});

  // Load related lists once
  useEffect(() => {
    const big = { page: 0, size: 9999, sort: 'id,asc', filters: [] };
    dispatch(getClases(big));
    dispatch(getWorkflows(big));
    dispatch(getGrupos({ page: 0, size: 9999, sort: 'nombre,asc', query: '' }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load entity for edit
  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Populate form from loaded entity
  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id: String(entity.id),
        empresa: String(entity.empresa?.id ?? ''),
        grupo: String(entity.grupoEmpresarial?.id ?? ''),
        workflow: String(entity.workflow?.id ?? ''),
        clase: String(entity.workflowClase?.id ?? ''),
        activo: String(entity.activo ?? true),
        disenoPersonalizado: String(entity.disenoPersonalizado ?? true),
        fechaInicio: entity.fechaInicio ?? '',
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate back on success
  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew
            ? 'Registro creado correctamente.'
            : 'Registro actualizado correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build autocomplete options from Redux lists
  const grupoOptions = useMemo(() => grupos.map(g => ({ value: String(g.id), label: g.nombre ?? `#${g.id}` })), [grupos]);
  const workflowOptions = useMemo(() => workflows.map(w => ({ value: String(w.id), label: w.nombre ?? `#${w.id}` })), [workflows]);
  const claseOptions = useMemo(() => clases.map(c => ({ value: String(c.id), label: c.nombre ?? `#${c.id}` })), [clases]);

  // Fields definition (inside component so options are reactive)
  const fields: FormField<WEForm>[] = useMemo(() => [
    {
      name: 'id',
      label: 'ID',
      readOnly: true,
      editOnly: true,
    },
    {
      name: 'empresa',
      label: 'Empresa',
      type: 'autocomplete-on-demand',
      required: true,
      placeholder: 'Buscar empresa…',
      url: 'api/empresas',
      queryParam: 'query',
      valueKey: 'id',
      labelKey: 'razonSocial',
      minChars: 2,
      extraParams: { page: '0', size: '20', sort: 'razonSocial,asc' },
      labelResolver: (val, raw) => raw ? `${raw.rfc} - ${raw.razonSocial}` : `${entity.empresa?.rfc} - ${entity.empresa?.razonSocial}`,
      renderOption: (raw, opt, isSelected) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>{raw.rfc}</span>
          <span style={{ fontWeight: isSelected ? 600 : 400 }}>{raw.razonSocial}</span>
          <span style={{ fontWeight: isSelected ? 600 : 400 }}>{raw.grupoEmpresarial.alias}</span>
        </div>
      ),
    },
    {
      name: 'workflow',
      label: 'Workflow',
      type: 'autocomplete-on-demand',
      required: true,
      placeholder: 'Buscar workflow…',
      url: 'api/profase-workflows',
      queryParam: 'query',
      valueKey: 'id',
      labelKey: 'nombre',
      minChars: 2,
      extraParams: { page: '0', size: '20', sort: 'nombre,asc' },
      labelResolver: (val, raw) => raw ? `${raw.nombre}` : `${entity.workflow?.nombre}`,
      renderOption: (raw, opt, isSelected) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontWeight: isSelected ? 600 : 400 }}>{raw.nombre}</span>
        </div>
      ),
    },
    {
      name: 'fechaInicio',
      label: 'Fecha de Inicio',
      type: 'date',
    },
    {
      name: 'activo',
      label: 'Activo',
      type: 'checkbox',
    },
    {
      name: 'disenoPersonalizado',
      label: 'Diseño Personalizado  ',
      type: 'checkbox',
    },
  ], [entity, grupoOptions, workflowOptions, claseOptions]);

  const handleFieldChange = (name: keyof WEForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const e2: Partial<Record<keyof WEForm, string>> = {};
    if (!form.empresa) e2.empresa = 'La empresa es requerida';
    if (Object.keys(e2).length) { setErrors(e2); return; }

    const payload: any = {
      empresa: { id: Number(form.empresa) },
      workflow: { id: Number(form.workflow) },
      activo: form.activo === 'true',
      fechaInicio: form.fechaInicio || null,
      disenoPersonalizado: form.disenoPersonalizado || null,
    };
    if (!isNew) payload.id = Number(id);

    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Workflow Empresa', to: BACK, icon: <FaNetworkWired /> },
        { label: isNew ? 'Nuevo Registro' : `Editar Registro #${id}` },
      ]} />

      <PageHeader
        icon={<FaNetworkWired />}
        iconColor="#14b8a6"
        title={isNew ? 'Nuevo Workflow Empresa' : 'Editar Workflow Empresa'}
        subtitle={isNew
          ? 'Asocia un workflow a una empresa'
          : `Modificando registro #${id}`}
      />

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#14b8a6"
      />
    </div>
  );
}
